import { useCallback } from 'react';
import * as React from 'react';

import { ButtonV2, ButtonV2ButtonProps } from 'component-lib/atoms/button-v2/ButtonV2';
import { useDisposables } from 'support/react/useDisposables';

import { useAbstractDropdownContext } from './AbstractDropdownContext';

export interface AbstractDropdownToggleProps extends Omit<ButtonV2ButtonProps, OmittedProps> {
  consumerName?: string;
  providerName?: string;
}

type OmittedProps = 'active' | 'id' | 'ref' | 'onClick' | 'onKeyDown' | 'type';

export function AbstractDropdownToggle({
  consumerName = 'AbstractDropdownToggle',
  providerName,
  ...forwardedProps
}: AbstractDropdownToggleProps) {
  const dropdown = useAbstractDropdownContext(consumerName, providerName);
  const disposables = useDisposables();

  const handleClick = useCallback(() => {
    dropdown.setIsExpanded(isExpanded => !isExpanded);
  }, [dropdown]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
        case 'Enter':
        case ' ':
          event.preventDefault();
          dropdown.setIsExpanded(true);

          // Wait for the items to render:
          disposables.requestAnimationFrame(dropdown.focusFirstItem);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          dropdown.setIsExpanded(true);

          // Wait for the items to render:
          disposables.requestAnimationFrame(dropdown.focusLastItem);
          break;
      }
    },
    [disposables, dropdown],
  );

  return (
    <ButtonV2
      {...forwardedProps}
      active={dropdown.isExpanded}
      id={dropdown.toggleID}
      ref={dropdown.setToggleElement}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      type="button"
    />
  );
}
